import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Grid,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { WideColorButton } from "../../components/ColorButtons/ColorButtons";
import withStyles from "@mui/styles/withStyles";
import { styled } from "@mui/styles/";
import PropTypes from "prop-types";

import PaymentsSchedule from "./../../components/PaymentsSchedule/PaymentsSchedule";
import { useHttp, httpPost, httpGet } from "../../hooks/http";
import { UserContext, ModalInfo } from "../../UserContext";
import { usePaymentModalReducer } from "../../reducers/reducers";
import PaymentModal from "../../components/PaymentModal/PaymentModal";
import { numberFormat } from "../../extends/NumberFormat";
import { useSnackbar } from "notistack";

const styles = {
  padding: {
    paddingLeft: 10,
    paddingRight: 10,
    marginTop: 10,
    width: "100%",
  },
  align: {
    textAlign: "center",
  },
  margin: {
    marginBottom: 40,
  },
  marginBottom: {
    marginBottom: 10,
    marginTop: 10,
  },
  paddingBox: {
    padding: 13,
  },
  heading: {
    fontWeight: "bold",
    marginLeft: 10,
  },
};

const TPPTabs = withStyles((theme) => ({
  indicator: {
    display: "none",
  },
  root: {
    backgroundColor: "#f5f5f5",
    minWidth: 250,
    [theme.breakpoints.up("sm")]: {
      marginRight: 10,
    },
  },
}))(Tabs);

const TPPTab = withStyles((theme) => ({
  root: {
    padding: 10,
    minHeight: 30,
    marginBottom: 5,
    borderRadius: 3,
    textTransform: "none",
    alignItems: "flex-start",
    "&:hover:not(.Mui-selected)": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
  },
  selected: {
    fontWeight: "bold",
    backgroundColor: "rgba(25, 115, 210, 0.1)",
  },
}))(Tab);

const PaymentCard = withStyles((theme) => ({
  root: {
    flex: "1 1 auto",
    [theme.breakpoints.down("md")]: {
      marginTop: 10,
    },
  },
}))(Card);

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

const PaymentContainer = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

const NavTabs = ({ header, selectedTab, onChange, className, children }) => {
  return (
    <div>
      {header && (
        <Typography
          marginBottom={1}
          fontSize={14}
          fontWeight="bold"
          color="textSecondary"
        >
          {header}
        </Typography>
      )}
      <TPPTabs
        orientation="vertical"
        variant="fullWidth"
        onChange={(_, newValue) => onChange && onChange(newValue)}
        value={selectedTab}
      >
        {children}
      </TPPTabs>
    </div>
  );
};

const sendClientComments = (clientId, comment, eventIds, token) => {
  return new Promise((resolve, reject) => {
    httpPost(
      `clientPortal/clients/${clientId}/payments/comment`,
      {
        eventIds: eventIds,
        comments: comment,
      },
      token
    ).then((result) => {
      if (result.loaded) {
        resolve();
        return;
      }
      reject();
    });
  });
};

const PaymentConfirmationDialog = ({
  open,
  onClose,
  transaction,
  userPreferredLanguage,
}) => {
  const border = {
    border: "1px solid #F6F6F9",
    borderRadius: 10,
    background: "#F6F6F9",
    padding: 13,
    marginBottom: 20,
  };
  const isCanadianFrench = userPreferredLanguage === "fr-CA";

  const fields = [
    {
      name: "Transaction Id",
      value: transaction?.transactionNo,
      fr: "Identifiant de transaction",
    },
    {
      name: "Amount",
      value: numberFormat(transaction?.amount || 0),
      fr: "Montant",
    },
    {
      name: "Payment Method",
      value: [transaction?.cardType, transaction?.cardNumber].join(" "),
      fr: "Méthode de paiement",
    },
    {
      name: "Auth Code",
      value: transaction?.authCode,
      fr: "Code d'autorisation",
    },
  ].map((field) => {
    if (!isCanadianFrench) {
      return field;
    }
    return {
      ...field,
      name: field.fr,
    };
  });

  return (
    <>
      <Dialog
        id="dlgPaymentConfirmation"
        open={open}
        maxWidth={"md"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle>
          {isCanadianFrench
            ? "Confirmation de paiement"
            : "Payment Confirmation"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isCanadianFrench
              ? `otre paiement a été traité avec succès et publié pour l'événement.`
              : `Your Payment was successfully processed and posted to the event.`}

            {transaction && (
              <Grid container direction="row" justifyContent="center">
                <Grid item xs={12} style={{ ...border }}>
                  {fields.map(({ name, value }) => (
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <div>
                        <strong>{name}</strong>:
                      </div>
                      <div>{value}</div>
                    </Stack>
                  ))}
                </Grid>
              </Grid>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <WideColorButton
            variant="outlined"
            size="small"
            onClick={() => {
              var content = document.getElementById("dlgPaymentConfirmation");
              var pri = window.open("", "_blank");
              pri.document.open();
              pri.document.write(content.innerHTML);
              pri.document.close();
              pri.focus();
              pri.print();
            }}
          >
            {isCanadianFrench ? "Imprimer" : "Print"}
          </WideColorButton>
          <WideColorButton variant="outlined" size="small" onClick={onClose}>
            {isCanadianFrench ? "D'accord" : "Ok"}
          </WideColorButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const sendAmplitude = (transaction) => {
  try {
    var processorId = transaction.processorId;
    var processorName = "";
    if (processorId === 0) {
      processorName = "X-Charge";
    } else if (processorId === 1) {
      processorName = "Authorize.Net";
    } else if (processorId === 2) {
      processorName = "Open Edge";
    } else if (processorId === 3) {
      processorName = "TPPpay";
    }
    transaction.invoiceInfo.forEach((payment) => {
      var hasProgramFee =
        processorId === 3 && payment.tppPayFeeAmount !== 0 ? true : false;
      var hasGratuity =
        processorId === 3 && payment.tppPayGratuityAmount !== 0 ? true : false;
      var paymentTotal =
        payment.amount + payment.tppPayFeeAmount + payment.tppPayGratuityAmount;
      var eventParams = {
        "processor name": processorName,
        "processor source": "Client Portal",
        "payment type": transaction.cardType,
        "has gratuity": hasGratuity,
        "has program fee": hasProgramFee,
        "payment total": paymentTotal,
        "payment subtotal": payment.amount,
        "event id": payment.partySak,
      };
      if (hasGratuity) {
        eventParams["gratuity total"] = payment.tppPayGratuityAmount;
        eventParams["gratuity percentage"] =
          payment.tppPayGratuityPercent !== -1
            ? payment.tppPayGratuityPercent
            : "";
        eventParams["custom entered gratuity"] =
          payment.tppPayGratuityPercent === -1 ? true : false;
      }
      if (hasProgramFee) {
        eventParams["program fee total"] = payment.tppPayFeeAmount;
        eventParams["program fee percentage"] = payment.tppPayFeePercentage;
      }
      window.amplitude.logEvent("processed payment", eventParams);
    });
  } catch {}
};

const Container = (props) => {
  const { classes, isAcceptPayment } = props;
  const { settings, access_token, contact, event, tppPaySettings } =
    useContext(UserContext);
  const [modal, dispatchModal] = usePaymentModalReducer();
  const [selectedTab, setSelectedTab] = useState(0);
  const [payments, setPayments] = useState([]);
  const selectedGratuity = useRef(-1);
  const [businessLocationGroups, setBusinessLocationGroups] = useState([]);
  const [transaction, setTransaction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userPreferredLanguage, setUserPreferredLanguage] = useState(null);
  const handleChange = (newValue) => {
    setSelectedTab(newValue);
  };
  const fetchDataUrl =
    isAcceptPayment && event
      ? `clientPortal/events/${event.id}/paymentEvents`
      : `clientPortal/clients/${contact.id}/paymentEvents`;
  const [loaded, fetchedData] = useHttp(fetchDataUrl, access_token, []);
  const { enqueueSnackbar } = useSnackbar();

  const eventId = useMemo(() => {
    return event?.id || contact?.id;
  }, [event?.id, contact?.id]);

  useEffect(() => {
    if (!loaded) {
      return;
    }
    setIsLoading(false);
    const _payments = fetchedData.events.map((element) => {
      element.showMore = false;
      element.otherAmount = "";
      element.otherAmountChecked = false;
      element.scheduledPayments.map((el) => {
        el.checked = false;
        return el;
      });
      return element;
    });
    setPayments(_payments);
  }, [loaded, fetchedData]);

  useEffect(() => {
    const businessLocations = payments
      .map((p) => p.event.businessLocation)
      .filter(Boolean);
    const uniqueIds = [...new Set(businessLocations.map((b) => b.id))];
    if (uniqueIds && uniqueIds.length > 0) {
      const groups = uniqueIds.map((id) => {
        const b = businessLocations.find((b) => b.id === id);
        return {
          id: b.id,
          name: b.name,
          payments: payments.filter(
            (p) => p.event.businessLocation.id === b.id
          ),
          settings: (
            tppPaySettings.find((s) => [b.id].indexOf(s.id) > -1) ||
            tppPaySettings[0]
          )?.settings,
        };
      });
      setBusinessLocationGroups(groups);
    }
  }, [payments, tppPaySettings]);

  const processPayments = (
    clientId,
    payments,
    token,
    businessLocationId,
    selGratuity,
    language = "en-US"
  ) => {
    setUserPreferredLanguage(language);
    dispatchModal({ type: "toogleModal", payload: true });
    dispatchModal({
      type: "setPaymentInfo",
      payload: {
        clientId,
        businessLocationId,
        payments,
      },
    });
    return new Promise((resolve, reject) => {
      selectedGratuity.current = selGratuity;
      resolve();
    });
  };
  const reloadPayments = useCallback(() => {
    return new Promise((resolve, reject) => {
      httpGet(fetchDataUrl, access_token).then((results) => {
        if (!results.loaded) {
          reject();
          return;
        }
        const _payments = results.response.events.map((element) => {
          element.showMore = false;
          element.otherAmount = "";
          element.otherAmountChecked = false;
          element.scheduledPayments.map((el) => {
            el.checked = false;
            return el;
          });
          return element;
        });
        resolve(_payments);
      });
    });
  }, [access_token, fetchDataUrl]);

  const BuisnessLocationGrid = () => {
    return (
      <Grid container justifyContent="center">
        <Grid container className={classes.padding}>
          <PaymentContainer>
            {businessLocationGroups.length > 1 && (
              <NavTabs
                header="Locations"
                selectedTab={selectedTab}
                onChange={(newValue) => handleChange(newValue)}
              >
                {businessLocationGroups.map((b, i) => (
                  <TPPTab
                    key={b.id}
                    label={`(${b.payments.length}) ${b.name}`}
                    {...a11yProps(i)}
                  />
                ))}
              </NavTabs>
            )}
            <PaymentCard>
              <CardContent>
                {businessLocationGroups.map((g, i) => {
                  if (selectedGratuity.current !== -1 && selectedTab === i) {
                    g.settings.selectedCurrentGratuity =
                      selectedGratuity.current;
                    selectedGratuity.current = -1;
                  }
                  return (
                    <TabPanel key={g.id} value={selectedTab} index={i}>
                      <PaymentsSchedule
                        applyTPPPayFee={g.settings.applyTPPPayFee}
                        applyACHPayFee={g.settings.applyTPPPayFeeToACH}
                        showACH={g.settings.showACH}
                        tppPayFeePercentage={g.settings.tppPayFeePercentage}
                        tppPayFeeLabel={g.settings.tppPayFeeLabel}
                        tppPayGratuityLabel={g.settings.additionalGratuityLabel}
                        applyTPPPayGratuity={g.settings.showAdditionalGratuity}
                        defaultGratuity1={g.settings.defaultGratuity1}
                        defaultGratuity2={g.settings.defaultGratuity2}
                        defaultGratuity3={g.settings.defaultGratuity3}
                        selectedDefaultGratuity={
                          g.settings.selectedCurrentGratuity !== undefined &&
                          g.settings.selectedCurrentGratuity !== -1
                            ? g.settings.selectedCurrentGratuity
                            : g.settings.selectedDefaultGratuity
                        }
                        payments={g.payments}
                        eventId={eventId}
                        token={access_token}
                        sendCommentCallback={sendClientComments}
                        country={g.settings?.country}
                        processPaymentCallback={(
                          payments,
                          selGratuity,
                          language
                        ) =>
                          processPayments(
                            contact?.id,
                            payments,
                            access_token,
                            g.id,
                            selGratuity,
                            language
                          )
                        }
                        fetchPaymentsCallback={() => {
                          return new Promise((resolve, reject) => {
                            httpGet(fetchDataUrl, access_token).then(
                              (results) => {
                                if (!results.loaded) {
                                  reject();
                                  return;
                                }
                                const payments = fetchedData.events.map(
                                  (element) => {
                                    element.showMore = false;
                                    element.otherAmount = "";
                                    element.otherAmountChecked = false;
                                    element.scheduledPayments.map((el) => {
                                      el.checked = false;
                                      return el;
                                    });
                                    return element;
                                  }
                                );
                                setPayments(payments);
                              }
                            );
                          });
                        }}
                      />
                    </TabPanel>
                  );
                })}
              </CardContent>
            </PaymentCard>
          </PaymentContainer>
        </Grid>
      </Grid>
    );
  };

  const SingleLocationGrid = () => {
    let paymentSettings = tppPaySettings[0]?.settings ?? settings;
    if (selectedGratuity.current !== -1) {
      paymentSettings.selectedCurrentGratuity = selectedGratuity.current;
    }
    const selectedDefaultGratuity =
      paymentSettings.selectedCurrentGratuity !== undefined &&
      paymentSettings.selectedCurrentGratuity !== -1
        ? paymentSettings.selectedCurrentGratuity
        : paymentSettings.selectedDefaultGratuity;
    return (
      <Grid container className={classes.padding}>
        <Paper className={classes.padding}>
          <Grid item xs={12}>
            <PaymentsSchedule
              applyTPPPayFee={paymentSettings.applyTPPPayFee}
              applyACHPayFee={paymentSettings.applyTPPPayFeeToACH}
              showACH={paymentSettings.showACH}
              tppPayFeePercentage={paymentSettings.tppPayFeePercentage}
              tppPayFeeLabel={paymentSettings.tppPayFeeLabel}
              tppPayGratuityLabel={paymentSettings.additionalGratuityLabel}
              applyTPPPayGratuity={paymentSettings.showAdditionalGratuity}
              defaultGratuity1={paymentSettings.defaultGratuity1}
              defaultGratuity2={paymentSettings.defaultGratuity2}
              defaultGratuity3={paymentSettings.defaultGratuity3}
              selectedDefaultGratuity={selectedDefaultGratuity}
              payments={payments}
              eventId={eventId}
              token={access_token}
              country={paymentSettings?.country}
              sendCommentCallback={sendClientComments}
              processPaymentCallback={(payments, selGratuity, language) =>
                processPayments(
                  contact?.id,
                  payments,
                  access_token,
                  null,
                  selGratuity,
                  language
                )
              }
            />
          </Grid>
        </Paper>
      </Grid>
    );
  };

  if (isLoading) {
    return (
      <Grid container justifyContent="center">
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }
  if (!payments || !payments.length) {
    return (
      <Grid container justifyContent="center">
        <Grid item>
          <Typography variant="h5"> You don't have Payments</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <ModalInfo.Provider value={{ modal, dispatchModal }}>
        {modal.open && (
          <PaymentModal
            userPreferredLanguage={userPreferredLanguage}
            onComplete={(transaction) => {
              if (transaction?.invoiceInfo) {
                setTransaction(transaction);
                sendAmplitude(transaction);
              }
              setIsLoading(true);
              reloadPayments().then((p) => {
                setIsLoading(false);
                setPayments(p);
              });
            }}
          />
        )}
      </ModalInfo.Provider>
      <PaymentConfirmationDialog
        userPreferredLanguage={userPreferredLanguage}
        open={Boolean(transaction)}
        onClose={() => {
          setTransaction(null);
        }}
        transaction={transaction}
      ></PaymentConfirmationDialog>
      {businessLocationGroups && businessLocationGroups.length > 0 ? (
        <BuisnessLocationGrid></BuisnessLocationGrid>
      ) : (
        <SingleLocationGrid></SingleLocationGrid>
      )}
    </>
  );
};

Container.propTypes = {
  classes: PropTypes.object.isRequired,
};
export default withStyles(styles)(Container);
